import { isHolidayString } from '../shared/utils/holidayHelper';
import { TIMEZONE } from '../shared/constants/timezone';
import { TARIH_UST_SINIR_YIL, TEKLIF_GECERLILIK_GUN } from '../shared/constants/businessRules';
/**
 * Centralized Timezone-Aware Date Utilities for Frontend
 * 
 * All date operations should go through this module to ensure consistent
 * timezone handling across the application.
 * 
 * Design Principles:
 * 1. Users are in Turkey (Europe/Istanbul)
 * 2. Explicitly specify timezone even though browser is likely in Turkey
 * 3. Using 'Europe/Istanbul' timezone ID handles DST history automatically
 *    (Turkey has been on permanent UTC+3 since 2016)
 *
 * SAAS-COUPLING: tarih/saat gösterim biçimi Türkçe yerel ayara ('tr-TR', GG.AA.YYYY)
 * sabitlenmiş — hardcoded — kiracı kaydından okunmalı; çıkarmada ülke bazlı tarih
 * biçimi seçimi buraya gelir. Etiket rota 3.35'te KONDU: bağlılık `format.ts`'in
 * başındaki notta anlatılıyordu ama `'tr-TR'`yi asıl yazan dosya burasıydı ve
 * hiçbir işaret taşımıyordu, yani etiket envanteri (grep'ten türer) bu dosyayı hiç
 * listelemiyordu. İkiz dosya `backend/src/utils/timeUtils.js`, para/sayı tarafı
 * `frontend/src/utils/format.ts`, saat dilimi kimliği `shared/constants/timezone`.
 * Dördü birlikte ele alınır (Etap 4).
 *
 * NOT: HTML tarih kutusunun KENDİ görünümü bu ayara bağlı değil — onu kullanıcının
 * işletim sistemi dili belirliyor. Ülke bazlı biçim gerçekten istenirse tarayıcının
 * hazır takvimi yerine kendi bileşenimiz gerekir; ayrı ve büyük bir iş.
 */

// Saat dilimi kimligi shared'da tek yerde (rota 2.4) — iki tarafin timeUtils'i
// ayni sabiti okur; SaaS'ta kiraci kaydina buradan tasinir.
export { TIMEZONE };

// ─── Takvim hesabi ─────────────────────────────────────────────────────────
// Gun, isletmenin takviminde bir gundur ('YYYY-MM-DD'); ekrana bakanin bilgisayar
// saatinden bulunmaz. Gun eklemek, haftanin gununu ve ayin son gununu bulmak icin
// gun UTC gece yarisina konup `getUTC*` ile geri okunur: bu hesaba ne saat dilimi
// ne yaz saati girer. Yerel getirici ve kurucu (`getDate`, `setDate`,
// `new Date(yil, ay, gun)`) bu dosyada da kullanilmaz; kaynak taramasi
// (`bicimlendirmeMerkezi.test.ts`) bu dosyayi da tarar.

const GUN_DESENI = /^(\d{4})-(\d{2})-(\d{2})$/;
const DUVAR_SAATI_DESENI = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

const utcGunu = (an: number): string => {
    const d = new Date(an);
    const ay = String(d.getUTCMonth() + 1).padStart(2, '0');
    const gun = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${ay}-${gun}`;
};

// Deger takvimde var mi: 2026-02-30, 13. ay ya da 25:99 yoktur. `Date.UTC` bunlari
// sessizce ileri tasir (2026-13-01 → 2027-01-01); uydurulmus bir tarih, bos bir
// degerden tehlikelidir. Yil dort haneli olmali, `toISODateTR` ile ayni kural.
const takvimdeVarMi = (yil: number, ay: number, gun: number, saat = 0, dakika = 0, saniye = 0): boolean => {
    if (yil < 1000 || yil > 9999) return false;
    const d = new Date(Date.UTC(yil, ay - 1, gun, saat, dakika, saniye));
    return d.getUTCFullYear() === yil && d.getUTCMonth() === ay - 1 && d.getUTCDate() === gun
        && d.getUTCHours() === saat && d.getUTCMinutes() === dakika && d.getUTCSeconds() === saniye;
};

// Girdinin isletme takvimindeki gunu. Gun dizgesi oldugu gibi alinir (ana cevrilip
// saat dilimine sokulmaz); an, isletmenin gunune cevrilir.
const takvimGunu = (baz: Date | string | null | undefined): string => {
    if (typeof baz === 'string') {
        const p = GUN_DESENI.exec(baz);
        if (p) return takvimdeVarMi(Number(p[1]), Number(p[2]), Number(p[3])) ? baz : '';
    }
    return timeUtils.toISODateTR(baz);
};

// 0 Pazar … 6 Cumartesi
const haftaninGunu = (gun: string): number => {
    const [yil, ay, g] = gun.split('-').map(Number);
    return new Date(Date.UTC(yil, ay - 1, g)).getUTCDay();
};

export const timeUtils = {
    /**
     * The timezone used throughout the application
     */
    TIMEZONE,

    /**
     * Get current date as YYYY-MM-DD string in Turkey timezone
     * Use for: date field defaults, comparisons
     * @returns {string} e.g., "2025-01-02"
     */
    todayTR: (): string => {
        return new Date().toLocaleDateString('sv-SE', { timeZone: TIMEZONE });
    },

    /**
     * Tarih alanlarının üst sınırı: bugünden N yıl sonrası, gün olarak (rota 3.35).
     *
     * Hesap burada çünkü gün sınırı işletmenin saat dilimine göre bulunmalı —
     * sabit `shared`da, takvim işi `timeUtils`te. Backend ikizi de aynı şekilde.
     * @returns {string} e.g., "2029-09-11"
     */
    tarihUstSiniri: (): string => {
        const bugun = new Date().toLocaleDateString('sv-SE', { timeZone: TIMEZONE });
        const [yil, ay, gun] = bugun.split('-');
        return `${Number(yil) + TARIH_UST_SINIR_YIL}-${ay}-${gun}`;
    },

    /**
     * Get current timestamp as zone-aware ISO 8601 string (UTC + Z).
     * Use for: siparis_tarihi default, datetime fields gönderen tüm payload'lar.
     *
     * Eskiden naive Istanbul wall-clock döndürüyordu ("2025-01-02T10:30:00"),
     * ama backend Joi `.date()` coercer'ı UTC sunucuda bunu UTC olarak yorumlayıp
     * +3 saat ileri kaydediyordu. UTC ISO + Z gönderince anlam belirsizliği kalmıyor.
     * @returns {string} e.g., "2025-01-02T07:30:00.000Z"
     */
    nowTR: (): string => {
        return new Date().toISOString();
    },

    /**
     * Convert any date to YYYY-MM-DD in Turkey timezone
     * Suitable for HTML date input values
     * @param {Date|string|null|undefined} input
     * @returns {string} YYYY-MM-DD or empty string if invalid
     */
    toISODateTR: (input: Date | string | null | undefined): string => {
        if (!input) return '';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '';
        const gun = d.toLocaleDateString('sv-SE', { timeZone: TIMEZONE });
        // Dört haneden kısa yıl BOŞ döner (rota 3.35). Çıktı doğrudan HTML
        // tarih kutusuna value olarak giriyor ve tarayıcı dört haneli olmayan
        // yılı reddetmek yerine AMERİKAN SIRASIYLA okuyor: canlıda yıl 1
        // tarihi taşıyan bir sipariş `1-12-31` üretti, ekranda 12.01.2031
        // göründü ve tek ok tuşuyla 2031 veritabanına yazıldı (ölçüldü).
        // Uydurulmuş bir tarih, boş bir kutudan tehlikelidir.
        //
        // ⚠️ Bu kural BİLEREK yalnız arayüz kopyasında. Backend'deki ikiz
        // (`backend/src/utils/timeUtils.js`) gösterim değil VERİ üretiyor —
        // Shopify, Hepsiburada ve webhook akışları kolona yazacak değeri ondan
        // alıyor. Orada boşa çevirmek sessiz veri kaybı olurdu.
        return gun.length < 10 ? '' : gun;
    },

    /**
     * Convert any date to ISO datetime in Turkey timezone
     * @param {Date|string|null|undefined} input
     * @returns {string} YYYY-MM-DDTHH:mm:ss or empty string if invalid
     */
    toISODateTimeTR: (input: Date | string | null | undefined): string => {
        if (!input) return '';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString('sv-SE', { timeZone: TIMEZONE }).replace(' ', 'T');
    },

    /**
     * Format date for Turkish display (DD.MM.YYYY)
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "02.01.2025" or "-" if invalid
     */
    formatDateTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('tr-TR', {
            timeZone: TIMEZONE,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    },

    /**
     * Format date for Turkish display short (DD.MM - no year)
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "02.01" or "-" if invalid
     */
    formatDateShortTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('tr-TR', {
            timeZone: TIMEZONE,
            day: '2-digit',
            month: '2-digit'
        });
    },

    /**
     * Format date for Turkish display (DD.MM.YYYY HH:mm)
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "02.01.2025 10:30" or "-" if invalid
     */
    formatDateTimeTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleString('tr-TR', {
            timeZone: TIMEZONE,
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    /**
     * Format date for Turkish display long (D MMMM YYYY)
     * Rapor dönem başlıkları — rota 2.1'e kadar beş rapor sayfasında ayrı ayrı yazılıydı.
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "2 Ocak 2025" or "-" if invalid
     */
    formatDateLongTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('tr-TR', {
            timeZone: TIMEZONE,
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    },

    /**
     * Format date long + weekday (D MMMM YYYY, dddd) — dönem başlığı
     * Virgül `Intl`'in birleşik çıktısında yok; iki parçadan kuruluyor ki
     * dashboard başlığı dize-özdeş kalsın (rota 2.1).
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "2 Ocak 2025, Perşembe" or "-" if invalid
     */
    formatDateLongWeekdayTR: (input: Date | string | null | undefined): string => {
        const tarih = timeUtils.formatDateLongTR(input);
        if (tarih === '-') return '-';
        const d = typeof input === 'string' ? new Date(input) : (input as Date);
        return `${tarih}, ${d.toLocaleDateString('tr-TR', { timeZone: TIMEZONE, weekday: 'long' })}`;
    },

    /**
     * Format month + year (MMMM YYYY) — dönem başlığı
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "Ocak 2025" or "-" if invalid
     */
    formatMonthYearTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('tr-TR', {
            timeZone: TIMEZONE,
            month: 'long',
            year: 'numeric'
        });
    },

    /**
     * Format date as day + short month + year (D MMM YYYY) — dönem aralığı ucu
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "2 Oca 2025" or "-" if invalid
     */
    formatDayMonthYearTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('tr-TR', {
            timeZone: TIMEZONE,
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    },

    /**
     * Format date as day + short month (D MMM) — grafik ekseni
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "2 Oca" or "-" if invalid
     */
    formatDayMonthTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleDateString('tr-TR', {
            timeZone: TIMEZONE,
            day: 'numeric',
            month: 'short'
        });
    },

    /**
     * Format time only (HH:mm)
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "10:30" or "-" if invalid
     */
    formatTimeTR: (input: Date | string | null | undefined): string => {
        if (!input) return '-';
        const d = typeof input === 'string' ? new Date(input) : input;
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleTimeString('tr-TR', {
            timeZone: TIMEZONE,
            hour: '2-digit',
            minute: '2-digit'
        });
    },

    /**
     * Format date + time, uzun ay adıyla (D MMMM YYYY, HH:mm)
     * Virgül `Intl`'in birleşik çıktısında yok; iki parçadan kuruluyor ki
     * kılavuzun "Son güncelleme" satırı dize-özdeş kalsın (rota 2.1).
     * @param {Date|string|null|undefined} input
     * @returns {string} e.g., "2 Ocak 2025, 10:30" or "-" if invalid
     */
    formatDateTimeLongTR: (input: Date | string | null | undefined): string => {
        const tarih = timeUtils.formatDateLongTR(input);
        if (tarih === '-') return '-';
        return `${tarih}, ${timeUtils.formatTimeTR(input)}`;
    },

    /**
     * Compare two dates by calendar day in Turkey timezone
     * @param {Date|string|null|undefined} date1
     * @param {Date|string|null|undefined} date2
     * @returns {boolean} True if same calendar day in TR timezone
     */
    areDatesEqualTR: (date1: Date | string | null | undefined, date2: Date | string | null | undefined): boolean => {
        if (!date1 || !date2) return false;
        const d1Str = timeUtils.toISODateTR(date1);
        const d2Str = timeUtils.toISODateTR(date2);
        if (!d1Str || !d2Str) return false;
        return d1Str === d2Str;
    },

    /**
     * Takvim gunune gun ekler (eksi deger geri gider).
     * Girdi gun dizgesiyse oldugu gibi alinir, ansa once isletmenin gunune cevrilir.
     * Saatli dizge saat farki tasimali (API'nin UTC ISO'su gibi); farksiz saat
     * tarayicinin saatiyle okunur.
     * @returns {string} 'YYYY-MM-DD'; bos, gecersiz ya da takvimde olmayan gunde,
     *   sonuc dort haneli yildan cikarsa ''
     */
    gunEkleTR: (baz: Date | string | null | undefined, gun: number): string => {
        const g = takvimGunu(baz);
        if (!g) return '';
        const [yil, ay, gn] = g.split('-').map(Number);
        const sonuc = utcGunu(Date.UTC(yil, ay - 1, gn + gun));
        return GUN_DESENI.test(sonuc) ? sonuc : '';
    },

    /**
     * Yeni teklifin varsayilan gecerlilik tarihi: bugunden `TEKLIF_GECERLILIK_GUN` gun sonrasi.
     * Teklif formu, kaydetmenin yedegi ve sifirlama dugmesi ayni yerden okur.
     */
    teklifGecerlilikTarihi: (): string => timeUtils.gunEkleTR(new Date(), TEKLIF_GECERLILIK_GUN),

    /**
     * Bugunun haftasi, Pazartesi-Pazar. Sunucunun "bu hafta"si da Istanbul'da
     * Pazartesi baslar (`dashboardService`, `DATE_TRUNC('week')`).
     */
    haftaAraligiTR: (): { baslangic: string; bitis: string } => {
        const bugun = timeUtils.todayTR();
        const pazartesi = timeUtils.gunEkleTR(bugun, -((haftaninGunu(bugun) + 6) % 7));
        return { baslangic: pazartesi, bitis: timeUtils.gunEkleTR(pazartesi, 6) };
    },

    /**
     * Ayin son gunu.
     * @param {number} ay 1-12
     */
    ayinSonGunu: (yil: number, ay: number): number => new Date(Date.UTC(yil, ay, 0)).getUTCDate(),

    /**
     * `toISODateTimeTR`'nin tersi: isletmenin saatiyle yazilmis 'YYYY-MM-DDTHH:mm[:ss]'
     * degerini (saat kutusunun degeri) sunucuya gidecek UTC ISO dizgesine cevirir.
     *
     * Saat farki sabit yazilmaz (fark sabit degildir; Turkiye de 2016'ya kadar yaz
     * saati uyguluyordu): o andaki fark kimlikten olculur, yaz saati gecisine denk
     * gelen deger icin olcum bir kez daha yapilir. Yaz saatine geciste var olmayan
     * saat bir saat kayik ana duser; Istanbul'da boyle saat yok.
     * @returns {string} UTC ISO; bicim ya da deger tutmazsa ''
     */
    fromISODateTimeTR: (duvar: string | null | undefined): string => {
        const p = DUVAR_SAATI_DESENI.exec(duvar || '');
        if (!p) return '';
        const [yil, ay, gun, saat, dakika, saniye] = p.slice(1).map((x) => Number(x || 0));
        if (!takvimdeVarMi(yil, ay, gun, saat, dakika, saniye)) return '';
        const hedef = Date.UTC(yil, ay - 1, gun, saat, dakika, saniye);
        // Verilen anda isletmenin duvar saati ile an arasindaki fark; olculemezse
        // sifir sayilmaz (o an uydurmak olurdu), sonuc bos doner.
        const fark = (an: number): number => {
            const q = DUVAR_SAATI_DESENI.exec(timeUtils.toISODateTimeTR(new Date(an)));
            if (!q) return NaN;
            const [y, m, d, h, mi, s] = q.slice(1).map((x) => Number(x || 0));
            return Date.UTC(y, m - 1, d, h, mi, s) - an;
        };
        let an = hedef - fark(hedef);
        an = hedef - fark(an);
        return Number.isNaN(an) ? '' : new Date(an).toISOString();
    }
};

// Named exports for convenience (excluding TIMEZONE which is already exported above)
export const {
    todayTR,
    tarihUstSiniri,
    nowTR,
    toISODateTR,
    toISODateTimeTR,
    formatDateTR,
    formatDateShortTR,
    formatDateTimeTR,
    formatDateLongTR,
    formatDateLongWeekdayTR,
    formatMonthYearTR,
    formatDayMonthYearTR,
    formatDayMonthTR,
    formatTimeTR,
    formatDateTimeLongTR,
    areDatesEqualTR,
    gunEkleTR,
    teklifGecerlilikTarihi,
    haftaAraligiTR,
    ayinSonGunu,
    fromISODateTimeTR
} = timeUtils;

export default timeUtils;


// [4.3] dateHelper.ts buraya taşındı — addBusinessDays artık timeUtils içinde

/**
 * Is gunu ekler; hafta sonu ve resmi tatil sayilmaz. Gunler isletmenin takviminde
 * sayilir, ekrana bakanin saatinde degil.
 * @returns {string} 'YYYY-MM-DD'; bos ya da gecersiz girdide ''
 *
 * Sunucudaki ikizi (`backend/src/utils/timeUtils.js`) Date dondurur ve gunu sunucu
 * saatinden sayar; ikisi bilerek ayri (ortak yer `shared/` olurdu, o da saat dilimi
 * sabitini iceri alamiyor).
 */
export const addBusinessDays = (date: Date | string, days: number): string => {
  let gun = takvimGunu(date);
  if (!gun) return '';
  let count = 0;
  let safetyCounter = 0;

  while (count < days && safetyCounter < 365) {
    gun = gunEkleTR(gun, 1);
    const haftaGunu = haftaninGunu(gun);
    if (haftaGunu !== 0 && haftaGunu !== 6 && !isHolidayString(gun)) {
      count++;
    }
    safetyCounter++;
  }
  return gun;
};
